import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import type { HouseholdCreateInput, HouseholdUpdateInput } from '@bbl/validation';
import { api, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/people/Pagination';
import type {
  HouseholdRecord,
  HouseholdWithStudents,
} from '../components/people/types';
import {
  HouseholdDrawer,
  type HouseholdDrawerMode,
} from '../components/people/HouseholdDrawer';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { timeAgo } from '../lib/format';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  householdsFilterConfig,
  HOUSEHOLDS_SORT_COLUMNS,
} from '../components/people/householdsFilterConfig';

const PAGE_SIZE = 20;
const householdsApi = api.crud<HouseholdRecord, HouseholdCreateInput, HouseholdUpdateInput>(
  '/api/households',
);

/**
 * URL search-param contract for the Households list. The toolkit is the single
 * source of truth for these values; the schema validates the raw URL at the
 * route boundary (everything optional, since a bare `/people/households` opens
 * on the configured defaults). Households are search-first: no status lifecycle
 * and no subject, so the only pill facet is `hasParentLogin`. Unknown values are
 * coerced/stripped by the toolkit's decoder, so this schema stays permissive on
 * shape and strict only on type.
 */
const householdsSearchSchema = z.object({
  q: z.string().optional(),
  sort: z.string().optional(),
  hasParentLogin: z.string().optional(),
  owesFees: z.string().optional(),
  hasActiveStudents: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_authed/people/households')({
  validateSearch: householdsSearchSchema,
  component: HouseholdsPage,
});

function HouseholdsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';

  const [drawer, setDrawer] = useState<HouseholdDrawerMode | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The adapter narrows the route's precisely-
  // typed search/navigate surface to the toolkit's structural `FilterRoute`
  // contract; the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigate = Route.useNavigate();
      return (opts) =>
        navigate({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(householdsFilterConfig, filterRoute, { pageSize: PAGE_SIZE });
  const { values, apiParams, setSort, setPage } = filters;

  const listQuery = useQuery({
    queryKey: ['households', apiParams] as const,
    queryFn: () => householdsApi.list(apiParams),
    placeholderData: (prev) => prev,
  });

  // Detail endpoint returns the household record plus students[]; the crud.get
  // helper hits GET /api/households/:id which the API extends with students.
  const householdDetailQuery = useQuery({
    queryKey: ['households', 'record', drawer?.kind === 'edit' ? drawer.id : null] as const,
    queryFn: () => {
      if (drawer?.kind !== 'edit') throw new Error('No family selected');
      return householdsApi.get(drawer.id) as Promise<HouseholdWithStudents>;
    },
    enabled: drawer?.kind === 'edit',
  });

  const columns = useMemo<ColumnDef<HouseholdRecord, unknown>[]>(() => {
    const cols: ColumnDef<HouseholdRecord, unknown>[] = [
      {
        id: 'primaryContactName',
        accessorKey: 'primaryContactName',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Primary contact"
            binding={HOUSEHOLDS_SORT_COLUMNS.name}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {info.row.original.primaryContactName}
          </span>
        ),
      },
      {
        id: 'primaryPhone',
        accessorKey: 'primaryPhone',
        enableSorting: false,
        header: 'Phone',
        cell: (info) => (
          <span className="text-[color:var(--color-text-muted)]">
            {info.row.original.primaryPhone}
          </span>
        ),
      },
      {
        id: 'primaryEmail',
        accessorKey: 'primaryEmail',
        enableSorting: false,
        header: 'Email',
        cell: (info) => (
          <span className="text-[color:var(--color-text-muted)]">
            {info.row.original.primaryEmail}
          </span>
        ),
      },
      {
        id: 'students',
        enableSorting: false,
        header: 'Students',
        cell: () => <span className="text-[color:var(--color-text-muted)]">—</span>,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Added"
            binding={HOUSEHOLDS_SORT_COLUMNS.createdAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {timeAgo(info.row.original.createdAt)}
          </span>
        ),
      },
    ];

    if (canManage) {
      cols.push({
        id: 'edit',
        enableSorting: false,
        header: () => <span className="sr-only">Edit</span>,
        cell: (info) => (
          <div className="flex justify-end">
            <EditIconButton
              label={`Edit ${info.row.original.primaryContactName}`}
              onClick={() => setDrawer({ kind: 'edit', id: info.row.original.id })}
            />
          </div>
        ),
      });
    }

    return cols;
  }, [canManage, values.sort, setSort]);

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/people/households">
    <ListShell
      title="Families"
      description="Parent accounts and the children linked to them."
      primaryAction={
        canManage ? (
          <Button
            onClick={() => setDrawer({ kind: 'create' })}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            Add family
          </Button>
        ) : undefined
      }
    >
      <FilterBar config={householdsFilterConfig} filters={filters} resultCount={total} />

      {listQuery.isError ? (
        <ErrorState
          title="Could not load families"
          description={
            listQuery.error instanceof ApiError
              ? listQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<HouseholdRecord>
            data={items}
            columns={columns}
            isLoading={isLoadingList}
            getRowId={(row) => row.id}
            onRowClick={(row) =>
              void navigate({
                to: '/people/households/$householdId',
                params: { householdId: row.id },
              })
            }
            emptyState={
              <EmptyState
                title="No families match these filters"
                description="Try clearing a filter, or add a family to give parents portal access and link their children."
                action={
                  canManage ? (
                    <Button
                      onClick={() => setDrawer({ kind: 'create' })}
                      icon={<Plus size={16} aria-hidden="true" />}
                    >
                      Add family
                    </Button>
                  ) : undefined
                }
              />
            }
          />
          <Pagination page={values.page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      {canManage ? (
        <HouseholdDrawer
          mode={drawer}
          onClose={() => setDrawer(null)}
          initial={householdDetailQuery.data ?? undefined}
          isLoadingInitial={drawer?.kind === 'edit' && householdDetailQuery.isLoading}
          loadError={householdDetailQuery.error instanceof Error ? householdDetailQuery.error : null}
          {...(drawer?.kind === 'edit'
            ? { detailLinkHref: `/people/households/${drawer.id}` }
            : {})}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ['households'] });
            toast('Family created. Activation email sent to parent.', { variant: 'success' });
            setDrawer(null);
          }}
          onUpdated={() => {
            void queryClient.invalidateQueries({ queryKey: ['households'] });
            toast('Family saved.', { variant: 'success' });
            setDrawer(null);
          }}
        />
      ) : null}
    </ListShell>
    </ListOrOutlet>
  );
}
